import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Sortable table header cell component
 */
export function SortableHeader({ 
  children, 
  sortKey, 
  currentSortKey, 
  sortOrder, 
  onSort, 
  className = "" 
}) {
  const isActive = currentSortKey === sortKey;
  
  return (
    <div 
      className={`flex items-center gap-1 cursor-pointer select-none hover:text-brillien-600 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-header-${sortKey}`}
    >
      <span className="font-semibold">{children}</span>
      <span className="w-4 h-4 flex items-center justify-center">
        {isActive ? (
          sortOrder === "asc" ? (
            <ChevronUp className="h-4 w-4 text-brillien-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-brillien-600" />
          )
        ) : (
          <ChevronsUpDown className="h-3 w-3 text-slate-400" />
        )}
      </span>
    </div>
  );
}

/**
 * Pagination component for tables
 */
export function TablePagination({
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  startIndex,
  endIndex,
  totalItems,
  pageSizeOptions = [10, 25, 50, 100]
}) {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (totalItems === 0) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
      <div className="flex items-center gap-4">
        <span className="text-sm text-slate-600">
          Showing <span className="font-medium">{startIndex}</span> to{" "}
          <span className="font-medium">{endIndex}</span> of{" "}
          <span className="font-medium">{totalItems}</span> results
        </span>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">Rows per page:</span>
          <Select 
            value={pageSize.toString()} 
            onValueChange={(val) => onPageSizeChange(Number(val))}
          >
            <SelectTrigger className="w-20 h-8" data-testid="page-size-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={size.toString()}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="h-8 px-2"
          data-testid="prev-page-btn"
        >
          Previous
        </Button>
        
        {getPageNumbers().map((page, idx) => (
          page === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400">...</span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? "default" : "outline"}
              size="sm"
              onClick={() => onPageChange(page)}
              className={`h-8 w-8 p-0 ${currentPage === page ? "bg-brillien-600 hover:bg-brillien-700" : ""}`}
              data-testid={`page-btn-${page}`}
            >
              {page}
            </Button>
          )
        ))}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="h-8 px-2"
          data-testid="next-page-btn"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

export default { SortableHeader, TablePagination };
